import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { passwordResetApi } from '../hooks/usePasswordResetApi';
import { ApiClientError } from '../lib/api-client';
import logo from '../assets/images/logo.svg';

/**
 * ResetPassword — page-level component for the `/reset-password` route.
 *
 * Reads the raw token from the `?token=` query string (the URL the user
 * clicked from the reset email). On success, redirects to `/login` with
 * a success flash so the user logs in with their new password.
 *
 * @see HZVT-150_ADD_AUTH_PASSWORD_RESET_AND_EMAIL_VERIFICATION
 */
export const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const tokenFromUrl = searchParams.get('token') ?? '';

  const [token, setToken] = useState(tokenFromUrl);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setSubmitting(true);
    try {
      await passwordResetApi.resetPassword({ token, newPassword });
      setDone(true);
      // Redirect to login after a short delay so the user can see the
      // success state (better UX than a hard navigate).
      setTimeout(() => navigate('/login', { replace: true }), 2000);
    } catch (err) {
      if (err instanceof ApiClientError && err.statusCode === 400) {
        setError('This reset link is invalid or has expired. Request a new one.');
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8">
        <div className="flex justify-center mb-6">
          <img src={logo} alt="ZuroQ Logo" className="h-12 w-auto" />
        </div>
        <h1 className="text-2xl font-bold text-center text-gray-900 mb-2">Reset Password</h1>
        <p className="text-center text-gray-600 text-sm mb-8">Choose a new strong password.</p>

        {done ? (
          <div
            data-testid="reset-password-success"
            className="bg-green-50 border border-green-200 rounded-md p-4 text-center text-green-800"
          >
            <p className="font-medium">Password updated.</p>
            <p className="text-sm mt-1">Redirecting you to login...</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            {!tokenFromUrl && (
              // When the user lands here without a `?token=` (e.g. by typing
              // the URL manually) we still accept the token via a regular
              // input so dev / QA can paste it. Hidden when the token came
              // from the URL to keep the UI tidy.
              <div className="grid w-full items-center gap-1.5">
                <Label htmlFor="token">Reset token</Label>
                <Input
                  type="text"
                  id="token"
                  placeholder="Paste the token from your email"
                  value={token}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setToken(e.target.value)}
                  required
                />
              </div>
            )}

            <div className="grid w-full items-center gap-1.5">
              <Label htmlFor="newPassword">New password</Label>
              <Input
                type="password"
                id="newPassword"
                placeholder="••••••••"
                value={newPassword}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setNewPassword(e.target.value)
                }
                required
                minLength={8}
              />
            </div>

            <div className="grid w-full items-center gap-1.5">
              <Label htmlFor="confirmPassword">Confirm new password</Label>
              <Input
                type="password"
                id="confirmPassword"
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setConfirmPassword(e.target.value)
                }
                required
                minLength={8}
              />
            </div>

            {error && (
              <div className="text-red-600 text-sm text-center" data-testid="reset-password-error">
                {error}
              </div>
            )}

            <Button type="submit" className="w-full" disabled={submitting || !token}>
              {submitting ? 'Resetting...' : 'Reset password'}
            </Button>
          </form>
        )}

        <p className="text-center text-sm text-gray-600 mt-6">
          <Link to="/login" className="font-medium text-indigo-600 hover:text-indigo-500">
            Back to login
          </Link>
        </p>
      </div>
    </div>
  );
};
